// @vendors
import { useContext, useState, useRef, useMemo} from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// @state
import { useUI } from "../../../../../app/context/ui";
import { EmployeeContext } from "../../../Employer";

// @assets
import { EmployeeTableStyles } from "../../../../../assets/css/employeeTable-style";
import { theme } from "../../../../../assets/theme";
import { GeneralStyles } from "../../../../../assets/css/general-style";

// @components
import DialogUI from "../../../../../components/shared/DialogUI";
import {
  ArrowForwardIcon,
  Box,
  Button,
  ClearAllIcon,
  FilterListRoundedIcon,
  IconButton,
  InputAdornment,
  SearchRoundedIcon,
  TextField,
  Tooltip,
  Link,
  Grid,
} from "../../../../../components/shared/MaterialUI";
import { AddNewEmployee } from "./AddEmployee";
import FilterStatus from "./FilterStatus";
import CustomDialog from "../../CustomDialog/CustomDialog";
import MenuAddBranch from "../../childs/MenuAddBranch";

// @helperts
import AppHelper from "../../../../../helpers/AppHelper";
import {
  downloadEmployees,
  getTemplateForCertification,
  isRyzeOrganization,
  permissionsForAddOrEditEmployees,
  permissionsForEditEmployer,
} from "../../../../../helpers/FunctionsUtils";

// @services
import SubmitterService from "../../../../../services/newApi/SubmitterServiceNewApi";
import CompanyService from "../../../../../services/newApi/CompanyServiceNewApi";

// @constants
import { ROUTENAME } from "../../../../../navigation/RouteName";
import { LISTROLES } from "../../../../../helpers/ListRoles";

const TableToolbar = () => {
  const {
    getAllEmployesByCompany,
    setRowsState,
    rowsState,
    companyId,
    setShowChildrens,
    listChildrens,
    childReachLimit,
    employerDetails,
    getAllChildrens,
    adminProps
  } = useContext(EmployeeContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const styles = useMemo(() => EmployeeTableStyles(),[]);
  const generalStyle = useMemo(() => GeneralStyles(), []);
  const id = open ? "simple-popover" : undefined;
  const [sendQuestionnaire, setSendQuestionnaire] = useState(false);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const openMenu = Boolean(anchorElMenu);
  const history = useHistory();
  const [isInactive, setIsInactive] = useState(false);

  const handleClickMenu = (event) => {
    setAnchorElMenu(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorElMenu(null);
  };

  const {
    blockUI,
    dialogUI,
    snackbarUI,
    progressUI,
  } = useUI();
  const customDialogRef = useRef(null);
  const globalState = useSelector((state) => state);
  const organization = globalState?.organizationReducer?.organizationSelected;
  const isRyze = organization?.name.toLowerCase().includes("ryze");
  const user = globalState.user;
  const companyService = useMemo(() => new CompanyService(), []);
  const submitterService = useMemo(() => new SubmitterService(), []);

  const submitSettings = {
    confirm: true,
    btn: {
      confirm: "Close",
      close: "",
    },
    onConfirm: () => dialogUI.current.close(),
    styles: { ...styles.dialog },
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const submitQuestionnaire = async () => {
    try {
      progressUI.current.open(
        "Your requests are being processed, please wait a moment",
        "",
        "success"
      );
      submitterService.setAccessToken(user.accessToken);
      const { data: all } = await submitterService.sendFormToState(
        companyId,
        user?.id
      );
      const { data } = all;

      progressUI.current.close();

      if (data.id) {
        return customDialogRef.current.open(
          "The forms have be successfully processed.",
          data
        );
      } else {
        dialogUI.current.open(
          "Thank you !",
          "There are no questionnaires ready for submission at this moment. Please try again later.",
          submitSettings
        );
      }
    } catch (e) {
      AppHelper.checkError(e, snackbarUI);
      progressUI.current.close();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const resendQuestionnaires = async () => {
    try {
      blockUI.current.open(true);
      const res = await companyService.resendQuestionnaire(companyId);
      const message = res.data.message;
      dialogUI.current.open("Congratulations!", message, submitSettings);
      setSendQuestionnaire(true);
      blockUI.current.open(false);
    } catch (e) {
      blockUI.current.open(false);
      AppHelper.checkError(e, snackbarUI);
    }
  };

  const onSelectChange = (status) => {
    setRowsState((prev) => ({
      ...prev,
      filters: {...prev.filters, status: status}
    }));
    const filters = { name: rowsState.filters.name, status: status };
    getAllEmployesByCompany({filters: filters});
    setAnchorEl(null);
  };

  const handleSearchBar = async (query) => {
    if (query) getAllEmployesByCompany();
  };

  const onSearchBarChange = (e) => {
    setRowsState((prev) => ({
      ...prev,
      filters: {...prev.filters, name: e.target.value}
    }));
  };

  const clearSearch = () => {
    setRowsState((prev) => ({
      ...prev,
      filters: {status: "", name: ""}
    }));
    setAnchorEl(null);
    getAllEmployesByCompany({filters: {}});
  };

  const actionToSelect = () => {
    if(rowsState.filters?.name === '')return clearSearch();
    if(rowsState.filters?.name?.length > 0)return handleSearchBar(rowsState.filters?.name);
    return
  }

  const getInactiveEmployees = (status) => {
    setIsInactive(status);
    if(status)return getAllEmployesByCompany({page: 1, size: 100, filters: {IsDeleted: status}});
    return getAllEmployesByCompany({filters: {}});
  }

  // user permissions for inactive employee 
  const permissionsInactiveEmployee = () => {
    const role = user.role
      if(role === LISTROLES.superAdmin || role === LISTROLES.orgManager )return true;
      return false;
  }

  const handleCertificationDenials = async () => {
    const params ={
      pathUrl: `/api/internal/v1/certifications/${companyId}`,
      token: user?.accessToken,
      fileName: `${adminProps?.name}-certification-denials.zip`,
      blockUI,
      snackbarUI
    }
    //download .zip file
    getTemplateForCertification(params);
  }

  return (
    <Box sx={styles.tableToolbar}>
      <Grid
        container
        direction="row"
        alignItems="center"
        spacing={2}
      >
        <Grid item xs={12} sm={4} md={3} lg={2}>
          <h2 style={{ color: theme.palette.primary.black }}>Employee list</h2>
        </Grid>

        <Grid
          item xs={12} sm={8} md={6} lg={5}
          sx={{display: 'flex', gap: '10px', alignItems: 'center'}}
        >
          <TextField
            sx={{
              ...styles.searchInput,
              ...generalStyle.customButtonSm,
              padding: 0
            }}
            value={rowsState.filters?.name}
            id="outlined"
            placeholder="Search"
            onChange={(e) => onSearchBarChange(e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if(rowsState.filters?.name === '') return clearSearch()
                return handleSearchBar(rowsState.filters?.name);
              }
            }}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <IconButton onClick={actionToSelect} sx={generalStyle.iconButtonSearch}>
                    <SearchRoundedIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Clear filter">
            <IconButton
              onClick={() => clearSearch()}
              sx={generalStyle.iconButtonSearch}
            >
              <ClearAllIcon />
            </IconButton>
          </Tooltip>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={2}>
          <Tooltip title="Show filters">
            <Button
              aria-describedby={id}
              onClick={handleClick}
              sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
            >
              Filter
              <FilterListRoundedIcon />
            </Button>
          </Tooltip>
        </Grid>

        {permissionsForAddOrEditEmployees(user?.role) &&
          <>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <AddNewEmployee
                companyId={companyId}
                getEmployees={() => getAllEmployesByCompany({stats:true})}
                hideExternalId={isRyze}
              />
            </Grid>          

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Tooltip title="Re-send questionnaires">
                <Button
                  variant="contained"
                  sx={{
                    ...generalStyle.customButton,
                    ...generalStyle.customButtonSm,
                    color: theme.palette.primary.main,
                    pointerEvents: isRyzeOrganization(organization) ? 'none' : 'auto'
                  }}
                  onClick={resendQuestionnaires}
                >
                  {sendQuestionnaire
                    ? "Re-sent questionnaires"
                    : "Re-send questionnaires"}
                  <ArrowForwardIcon />
                </Button>
              </Tooltip>
            </Grid>

            <Grid
              item xs={6} sm={6} md={4} lg={2}
              sx={{pointerEvents: isRyzeOrganization(organization) ? 'none' : 'auto'}}
            >
              <Tooltip title="Export">
                <Link
                  download
                  onClick={() => downloadEmployees(companyId, blockUI, snackbarUI)}
                  sx={styles.exportLink}
                >
                  <Button
                    variant="contained"
                    component="span"
                    sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
                  >
                    Export
                  </Button>
                </Link>
              </Tooltip>
            </Grid>

            <Grid item xs={6} sm={4} md={3} lg={2}>
              <Tooltip title="Submit">
                <Button
                  variant="contained"
                  sx={{
                    ...generalStyle.customButtonSm,
                    ...generalStyle.customButton,
                    background: theme.palette.primary.main,
                    color: theme.palette.primary.white,
                    pointerEvents: isRyzeOrganization(organization) ? 'none' : 'auto',
                    '&:hover': {
                      background: theme.palette.primary.main,
                      transform: 'translateY(-2px)',
                      boxShadow: theme.button.boxShadowHover
                    },
                  }}
                  onClick={submitQuestionnaire}
                >
                  Submit
                  <ArrowForwardIcon />
                </Button>
              </Tooltip>
            </Grid>

            {!childReachLimit && permissionsForEditEmployer(user?.role)
              ? <Grid
                  item xs={6} sm={4} md={3} lg={2}
                  sx={{pointerEvents: isRyzeOrganization(organization) ? 'none' : 'auto'}}
                >
                  <Tooltip title="Add new branch">
                    <Button
                      variant="contained"
                      sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
                      onClick={handleClickMenu}
                      aria-controls={openMenu ? 'basic-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openMenu ? 'true' : undefined}
                    >
                      Add new branch
                    </Button>
                  </Tooltip>
                </Grid>
              : null
            }
          </>
        }

        {listChildrens?.rows?.length
          ? <Grid item xs={6} sm={4} md={3} lg={2}>
              <Tooltip title="Show children">
                <Button
                  variant="contained"
                  sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
                  onClick={() => setShowChildrens(true)}
                >
                  show children
                </Button>
              </Tooltip>
            </Grid>
          : null
        }

        {!listChildrens?.rows?.length && employerDetails?.data?.parentCompanyId
          ? <Grid item xs={6} sm={4} md={3} lg={2}>
              <Tooltip title="Back to parent">
                <Button
                  variant="contained"
                  sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
                  onClick={() => {
                    history.push(`${ROUTENAME.employer}${employerDetails?.data?.parentCompanyId}`)
                    getAllChildrens(employerDetails.data.parentCompanyId)
                }}
                >
                   back to parent
                </Button>
              </Tooltip>
            </Grid>
          : null
        }
        
        {employerDetails?.data?.mainParentCompanyId
          ? <Grid item xs={6} sm={4} md={3} lg={2}>
              <Tooltip title="Back to main parent">
                <Button
                  variant="contained"
                  sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
                  onClick={() => {
                    history.push(`${ROUTENAME.employer}${employerDetails?.data?.mainParentCompanyId}`);
                    getAllChildrens(employerDetails.data.mainParentCompanyId);
                  }}
                >
                  back to main parent
                </Button>
              </Tooltip>
            </Grid>
          : null
        }

        {permissionsInactiveEmployee()
          ? <Tooltip title={isInactive ? "Show active employees" : "Show inactive employees"}>
              <Grid item xs={6} sm={4} md={3} lg={2}>
                <Button
                  variant="contained"
                  component="span"
                  sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
                  onClick={() => getInactiveEmployees(!isInactive)}
                >
                  {!isInactive ? 'Inactive' : 'Active'}
                </Button>
              </Grid>
            </Tooltip>
          : null  
        }

        <Grid
          item xs={6} sm={4} md={3} lg={2}
          sx={{pointerEvents: isRyzeOrganization(organization) ? 'none' : 'auto'}}
        >
          <Tooltip title="Certifications-denials files">
            <Button
              variant="contained"
              sx={{...generalStyle.customButton, ...generalStyle.customButtonSm}}
              onClick={handleCertificationDenials}
            >
             Certifications denials
            </Button>
          </Tooltip>
        </Grid>

        <FilterStatus
          id={id}
          open={open}
          anchorEl={anchorEl}
          handleClose={handleClose}
          onSelectChange={onSelectChange}
          clearSearch={clearSearch}
        />
      </Grid>
      <DialogUI ref={dialogUI} companyId={companyId} />
      <CustomDialog onUpdate={getAllEmployesByCompany} ref={customDialogRef} />
      <MenuAddBranch
          anchorElMenu={anchorElMenu}
          openMenu={openMenu}
          handleCloseMenu={handleCloseMenu}
      />
    </Box>
  );
};

export default TableToolbar;
